import { ChangeEvent } from 'react';
import { useTranslate } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useResponsive } from 'src/hooks/use-responsive';
import { decimalToPercent, percentToDecimal } from 'src/pages/admin/utils/decimal-input';

import { formatFixedDecimals, normalizeNumericInput } from '../params/helpers';
import { OFFICIAL_VAULTS_NS as NS, STRATEGY_MAX, STRATEGY_MIN, WIRE_DECIMALS } from './constants';
import { formatUsdcAmount } from '../vaults/helpers';
import { FIELD_LABELS, RequestBounds, RequestDraftKey, sizeCapFor, usdcText } from './helpers';
import type { OfficialVaultForm } from './useOfficialVaults';

type Props = {
  form: Pick<
    OfficialVaultForm,
    | 'open'
    | 'step'
    | 'retrying'
    | 'draft'
    | 'errors'
    | 'alert'
    | 'submitting'
    | 'closeDialog'
    | 'setField'
    | 'review'
  >;
  bounds: RequestBounds | null;
};

/** A field the operator cannot type into reads as such. */
const READ_ONLY_SX = { '& .MuiInputBase-root': { bgcolor: 'action.hover' } } as const;

const NUMERIC: Partial<Record<RequestDraftKey, 'decimal' | 'numeric'>> = {
  feePercent: 'decimal',
  lockupDays: 'numeric',
  stakeAmount: 'decimal',
};

export const RequestDialog = ({ form, bounds }: Props) => {
  const translate = useTranslate();
  const smDown = useResponsive('down', 'sm');

  const fee = form.draft.feePercent.trim();
  const feeFraction = fee ? percentToDecimal(fee) : null;
  const hints: Partial<Record<RequestDraftKey, string>> = {
    vaultId: translate(`${NS}.form.vaultIdHint`),
    strategy: translate(`${NS}.form.strategyLength`, {
      min: STRATEGY_MIN,
      max: STRATEGY_MAX,
      count: Array.from(form.draft.strategy.trim()).length,
    }),
    feePercent: [
      bounds &&
        translate(`${NS}.form.allowedFee`, { cap: decimalToPercent(bounds.performanceFeeCap) }),
      feeFraction !== null &&
        translate(`${NS}.form.sentAs`, { value: formatFixedDecimals(feeFraction, WIRE_DECIMALS) }),
    ]
      .filter(Boolean)
      .join(' · '),
    lockupDays: bounds
      ? translate(`${NS}.form.allowedLockup`, {
          min: bounds.lockupMinDays,
          max: bounds.lockupMaxDays,
        })
      : undefined,
    xHandle: translate(`${NS}.form.optional`),
    groupChatId: translate(`${NS}.form.optional`),
    managerName: translate(`${NS}.form.managerNameHint`),
    managerHolderId: translate(`${NS}.form.managerHolderIdHint`),
    managerAddress: translate(`${NS}.form.managerAddressHint`),
    stakeAmount: bounds
      ? translate(`${NS}.form.allowedStake`, { min: formatUsdcAmount(bounds.minSubscription) })
      : undefined,
  };

  const sizeCap = bounds ? sizeCapFor(form.draft.stakeAmount.trim(), bounds.ownershipFloor) : null;

  /** A value the operator cannot type: the generated ID, the recorded floor, the computed cap. */
  const readOnly = (label: string, value: string, helperText?: string) => (
    <TextField
      key={label}
      fullWidth
      size="small"
      label={translate(label)}
      value={value}
      helperText={helperText}
      InputProps={{ readOnly: true }}
      InputLabelProps={{ shrink: true }}
      sx={READ_ONLY_SX}
    />
  );

  const field = (
    key: RequestDraftKey,
    { full = false, required = true, multiline = false } = {}
  ) => {
    const error = form.errors[key];
    const mode = NUMERIC[key];
    const unit = key === 'feePercent' ? '%' : key === 'stakeAmount' ? 'USDC' : undefined;
    return (
      <TextField
        key={key}
        fullWidth
        size="small"
        required={required}
        label={translate(FIELD_LABELS[key])}
        value={form.draft[key]}
        error={Boolean(error)}
        helperText={error ? [translate(error), hints[key]].filter(Boolean).join(' · ') : hints[key]}
        // Only IME full-width digits are normalised; anything else stays as typed for validation.
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          form.setField(key, mode ? normalizeNumericInput(e.target.value) : e.target.value)
        }
        multiline={multiline}
        minRows={multiline ? 2 : undefined}
        inputProps={mode ? { inputMode: mode } : undefined}
        InputProps={{
          // The vault ID is generated from the name (PO, 2026-10-01); its errors still show here.
          readOnly: key === 'vaultId',
          ...(unit && { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> }),
        }}
        InputLabelProps={key === 'vaultId' ? { shrink: true } : undefined}
        sx={{ ...(full && { gridColumn: '1 / -1' }), ...(key === 'vaultId' && READ_ONLY_SX) }}
      />
    );
  };

  return (
    <Dialog
      open={form.open && form.step === 'edit'}
      onClose={form.submitting ? undefined : form.closeDialog}
      maxWidth="sm"
      fullWidth
      fullScreen={smDown}
    >
      <DialogTitle>{translate(`${NS}.form.title`)}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="warning">{translate(`${NS}.form.immutableWarning`)}</Alert>
          {form.retrying && <Alert severity="info">{translate(`${NS}.form.retryHint`)}</Alert>}
          {form.alert && (
            <Alert severity={form.alert.severity}>
              <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
                {form.alert.messages.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </Stack>
            </Alert>
          )}
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            }}
          >
            {/* The PO's order (2026-10-01): identity, manager, strategy, money, terms, bindings, derived. */}
            {field('name')}
            {field('vaultId')}
            {field('managerName')}
            {field('managerHolderId')}
            {field('managerAddress', { full: true })}
            {field('strategy', { full: true, multiline: true })}
            {field('stakeAmount', { full: true })}
            {field('feePercent')}
            {field('lockupDays')}
            {field('xHandle', { required: false })}
            {field('groupChatId', { required: false })}
            {readOnly(
              FIELD_LABELS.ownershipFloor,
              bounds ? `${decimalToPercent(bounds.ownershipFloor)}%` : '',
              translate(`${NS}.form.ownershipFloorHint`)
            )}
            {readOnly(
              FIELD_LABELS.sizeCap,
              sizeCap === null ? '' : usdcText(sizeCap),
              translate(`${NS}.form.sizeCapHint`)
            )}
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {translate(`${NS}.form.stakeNote`)}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={form.closeDialog} disabled={form.submitting}>
          {translate(`${NS}.form.cancel`)}
        </Button>
        <Button variant="contained" onClick={form.review} disabled={form.submitting}>
          {translate(`${NS}.form.review`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
